import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as types from '../../constants/actionTypes';
import Drawer from '../Common/Drawer/Drawer';
import BaselineTaskForm from './BaselineTaskForm';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../../components/Common/Notification';
import { DATE_FORMAT } from '../../constants/constants';
import Moment from 'moment';
import _ from 'lodash';

export default class BaselineTaskDrawer extends Component {
	constructor(props, context) {
		super(props, context);

		const newTask = {
			id: 0,
			name: '',
			contingentId: '',
			phaseId: '',
			departmentId: '',
			communityId: '',
			jobTitleId: '',
			contingentName: '',
			baselineId: this.props.baselineId,
			contactName: '',
			contactEmail: '',
			additionalInfo: '',
			taskTagIds: [],
			isActive: true,
			isLocationTaskListActive: true,
			isLocationTaskDateAdjustable: props.formFilters.isProjectWidgetActive ? false : true,
			links: [],
			targetDaysOut: null,
			targetDays: null,
			targetWeeks: null,
			targetStaticDate: '',
			startDaysOut: null,
			startDays: null,
			startWeeks: null,
			startStaticDate: '',
			anticipatedEffort: '',
			steps: [],
			prereqTasks: [],
			workflows: [],
			showStaticDates: false, // only for ui
			createdByName: '', // Only display if Editing
			createdDate: '', // Only display if Editing
			modifiedByName: '', // Only display if Editing
			modifiedDate: '', // Only display if Editing
			callWebhookOnStatusChange: false,
			isBaselineTaskValidationRulesActive: props.formFilters.isBaselineTaskValidationRulesActive
		};

		this.state = {
			editTask: newTask,
			unchangedTask: newTask,
			newTask: newTask,
			isFormValid: true,
			showDateError: false,
			isLoading: false,
			taskSearchResults: [],
			taskPrereqSearchResults: [],
			unsavedChanges: false,
			saveAndNewRetention: false,
		};
	}

	async componentDidMount() {
		await this.loadTask(this.props.taskId);
	}

	componentDidUpdate(prevProps) {
		if (this.props.taskId !== prevProps.taskId) {
			this.loadTask(this.props.taskId);
		}
	}

	loadTask = async (taskId) => {
		if (taskId == null) {
			return;
		}
		try {
			this.setState({ isLoading: true });
			const response = await this.props.baselineTaskActions.loadBaselineTask(taskId);
			if (response.type === types.LOAD_BASELINE_TASK_SUCCESS) {

				let task = response.payload;

				// we either show static dates (datepickers) or target dates (inputs)
				// if either one of the static dates exists then default to showing those instead
				if (task.targetStaticDate || task.startStaticDate) {
					task.showStaticDates = true;
				}

				// format dates for datepicker
				if (task.targetStaticDate) {
					const targetDate = new Date(task.targetStaticDate);
					task.targetStaticDate = Moment.utc(targetDate).format(DATE_FORMAT);
				}

				if (task.startStaticDate) {
					const startDate = new Date(task.startStaticDate);
					task.startStaticDate = Moment.utc(startDate).format(DATE_FORMAT);
				}

				// format numerical values for target inputs (values can be negative!)
				// the value in the db is the total days, but the ui splits that total up between days and weeks
				task.targetWeeks = parseInt(task.targetDaysOut / 7);
				task.targetDays = task.targetWeeks != 0 ? task.targetDaysOut % 7 : task.targetDaysOut;

				task.startWeeks = parseInt(task.startDaysOut / 7);
				task.startDays = task.startWeeks != 0 ? task.startDaysOut % 7 : task.startDaysOut;

				// Format audit dates just to match the rest of the date formatting
				const dateCreated = new Date(task.createdDate);
				task.dateCreated = Moment.utc(dateCreated).format(DATE_FORMAT);
				const dateModified = task.modifiedDate ? new Date(task.modifiedDate) : '';
				task.dateModified = dateModified ? Moment.utc(dateModified).format(DATE_FORMAT) : '';

				const newTask = { ...this.state.newTask, ...task };

				if (!newTask.links || !newTask.links.length) {
					newTask.links == this.state.newTask.links; // Ensure always a blank link ready for editing
				}
				this.setState({ editTask: newTask, unchangedTask: newTask, isDrawerOpen: true, isFormValid: true });
			} else if (response.type === types.LOAD_BASELINE_TASK_FAILURE) {
				Notification('error', 'An Error Occurred', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			this.setState({ isLoading: false });
		}
	};

	searchPrereqTasks = async (searchTerm) => {
		try {
			const response = await this.props.baselineTaskActions.optionSearch(
				searchTerm,
				this.state.editTask.baselineId,
				this.state.editTask.id,
				true
			);
			if (response.type === types.OPTION_SEARCH_BASELINE_TASK_SUCCESS) {
				this.setState({ taskPrereqSearchResults: response.payload.results });
			} else if (response.type === types.OPTION_SEARCH_BASELINE_TASK_FAILURE) {
				Notification('error', 'An Error Occurred', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	searchTasks = async (searchTerm) => {
		try {
			const response = await this.props.baselineTaskActions.optionSearch(
				searchTerm,
				this.state.editTask.baselineId,
				this.state.editTask.id,
				false
			);
			if (response.type === types.OPTION_SEARCH_BASELINE_TASK_SUCCESS) {
				this.setState({ taskSearchResults: response.payload.results });
			} else if (response.type === types.OPTION_SEARCH_BASELINE_TASK_FAILURE) {
				Notification('error', 'An Error Occurred', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	onFormChange = (name, value) => {
		let newTask = {
			...this.state.editTask,
			[name]: value,
		};

		if (name === 'isActive' && value === false) {
			// #1249 ensure the default is false
			newTask = {
				...newTask,
				isLocationTaskListActive: false,
			};
		} else if (name === 'showStaticDates') {
			// statics dates cannot be locked, days out has a choice but set the default here when they switch
			newTask = {
				...newTask,
				isLocationTaskDateAdjustable: value === true ? false : true,
			};
		}

		this.setState({ editTask: newTask }, () => {
			this.checkForChanges();
		});
	};

	onContingentChange = async (contingentId, contingentName) => {
		if (contingentId === this.state.editTask.id) {
			Notification('error', 'Invalid Contingent', 'You can not make this task a contingent upon itself.');
			return;
		}

		try {
			const response = await this.props.baselineTaskActions.updateContingent(this.state.editTask.id, contingentId);
			if (response.type === types.UPDATE_BASELINE_TASK_CONTINGENT_SUCCESS) {
				Notification('success', '', 'Contingent Updated');
				this.setState({ editTask: { ...this.state.editTask, contingentId: contingentId, contingentName: contingentName } }, () => {
					this.checkForChanges();
				});
			} else if (response.type === types.UPDATE_BASELINE_TASK_CONTINGENT_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	onContingentRemove = async () => {
		try {
			const response = await this.props.baselineTaskActions.updateContingent(this.state.editTask.id, null);
			if (response.type === types.UPDATE_BASELINE_TASK_CONTINGENT_SUCCESS) {
				Notification('success', '', 'Contingent Removed');
				this.setState({ editTask: { ...this.state.editTask, contingentId: '', contingentName: '' } }, () => {
					this.checkForChanges();
				});
			} else if (response.type === types.UPDATE_BASELINE_TASK_CONTINGENT_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	onPrereqChange = async (prereqId) => {
		try {
			const response = await this.props.baselineTaskActions.updateContingent(prereqId, this.state.editTask.id);
			if (response.type === types.UPDATE_BASELINE_TASK_CONTINGENT_SUCCESS) {
				Notification('success', '', 'Prereq Added');
				let prereqTasks = [];
				if (prereqId) {
					const task = this.state.taskPrereqSearchResults.find((x) => x.id === prereqId);
					prereqTasks = [...this.state.editTask.prereqTasks, { id: task.id, name: task.name }];
				}

				this.setState({ editTask: { ...this.state.editTask, prereqTasks: prereqTasks } }, () => {
					this.checkForChanges();
				});
			} else if (response.type === types.UPDATE_BASELINE_TASK_CONTINGENT_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	onRemovePrereq = async (prereqId) => {
		try {
			const response = await this.props.baselineTaskActions.updateContingent(prereqId, null);
			if (response.type === types.UPDATE_BASELINE_TASK_CONTINGENT_SUCCESS) {
				Notification('success', '', 'Prereq Added');
				const index = this.state.editTask.prereqTasks.indexOf(prereqId);
				const prereqTasks = this.state.editTask.prereqTasks.slice();
				prereqTasks.splice(index, 1);

				this.setState({ editTask: { ...this.state.editTask, prereqTasks: prereqTasks } }, () => {
					this.checkForChanges();
				});
			} else if (response.type === types.UPDATE_BASELINE_TASK_CONTINGENT_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	checkForChanges = () => {
		// compare unChangedTask to editTask if editing a task
		if (this.state.editTask.id != 0) {
			const haveTheSameValues = _.isEqual(this.state.unchangedTask, this.state.editTask);
			this.setState({ unsavedChanges: !haveTheSameValues });
		}
	};

	validateForm = (task) => {
		const { name, jobTitleId, departmentId, communityId } = task;

		// if there are steps, make sure they all have a name
		for (let i = 0; i < task.steps.length; i++) {
			if (task.steps[i].name == '') {
				return false;
			}
		}

		for (let i = 0; i < task.links.length; i++) {
			const link = task.links[i];
			if (link.url.trim().length > 0 && link.name.trim().length === 0) {
				// if there are links, make sure they all have abbreviations
				// if there is white space clear it out so element validation is visible
				link.name = '';
				let links = task.links.slice();
				links.splice(i, 1, link);
				this.onFormChange('links', links);
				return false;
			}
		}

		if (this.state.editTask.showStaticDates && !this.state.editTask.targetStaticDate) {
			return false;
		}

		if (this.state.editTask.showStaticDates === false && (isNaN(this.state.editTask.targetWeeks) || this.state.editTask.targetWeeks === '')) {
			return false;
		}

		if (this.state.editTask.showStaticDates === false && (isNaN(this.state.editTask.targetDays) || this.state.editTask.targetDays === '')) {
			return false;
		}

		if (name && name.trim().length > 0 && jobTitleId && departmentId && communityId) {
			return true;
		}

		return false;
	};

	onSaveAndNew = () => {
		this.setState({ saveAndNewRetention: true }, this.onSave);
	};

	parseOrNull = (value) => {
		const parsed = parseInt(value);
		return value === null || value === '' || isNaN(parsed) ? null : parsed;
	};	

	onSave = async () => {
		let task = { ...this.state.editTask };

		if (!this.validateForm(task)) {
			this.setState({ isFormValid: false });
			return;
		}

		if (this.state.editTask.showStaticDates) {
			// Clear out any values in the other section that isn't visible so it doesn't get sent to API
			task.targetDaysOut = null;
			task.startDaysOut = null;
			task.targetDays = null;
			task.targetWeeks = null;
			task.startDays = null;
			task.startWeeks = null;
			task.isLocationTaskDateAdjustable = false;
		
			// Validate static dates only if start is active
			if (this.props.formFilters.isStartDateActive) {
				if (task.startStaticDate?.length > 0 || task.targetStaticDate?.length > 0) {
					const start = Date.parse(task.startStaticDate);
					const target = Date.parse(task.targetStaticDate);
		
					// You can start and finish on the same day, but not target before start
					if (target < start) {
						this.setState({ showDateError: true });
						return;
					}
				}
			}
		} else {
			// Days and weeks path
			const targetWeeks = this.parseOrNull(task.targetWeeks);
			const targetDays = this.parseOrNull(task.targetDays);
			const startWeeks = this.parseOrNull(task.startWeeks);
			const startDays = this.parseOrNull(task.startDays);

			// Convert target dates to total days — 0 and negative are allowed
			if (targetWeeks === null && targetDays === null) {
				task.targetDaysOut = null;
			} else {
				task.targetDaysOut = (targetWeeks || 0) * 7 + (targetDays || 0);
			}

			// Convert start dates to total days — 0 and negative are allowed
			if (startWeeks === null && startDays === null) {
				task.startDaysOut = null;
			} else {
				task.startDaysOut = (startWeeks || 0) * 7 + (startDays || 0);
			}

			// Clear static dates
			task.startStaticDate = null;
			task.targetStaticDate = null;

			if (this.props.formFilters.isStartDateActive) {
				// Allow 0 and negative values
				if (task.startDaysOut !== null && task.targetDaysOut !== null) {
					// Validate: target must not be before start
					if (task.targetDaysOut > task.startDaysOut) {
						this.setState({ showDateError: true });
						return;
					}
				}
			} else {
				// Start date not active — clear it
				task.startDaysOut = null;
			}
		}

		// if IsActive make sure isLocationTaskListActive is true
		if (task.isActive) {
			task.isLocationTaskListActive = true;
		}

		// Remove any blank links
		if (task.links && task.links.length) {
			task.links = task.links.filter((x) => x.name && x.name.trim().length && x.url && x.url.trim().length);
		}

		//transforms taskTagIds from string to array of strings
		if (task.taskTagIds && task.taskTagIds.length > 0) {
			task.taskTagIds = !Array.isArray(task.taskTagIds) ? task.taskTagIds.split(',') : task.taskTagIds.join().split(',');
		}

		this.setState({ isFormValid: true, isLoading: true });

		if (task.id === 0) {
			try {
				const response = await this.props.baselineTaskActions.saveBaselineTask(task);
				if (response.type === types.CREATE_BASELINE_TASK_SUCCESS) {
					Notification('success', 'Success', 'Baseline Task Created');
					if (this.state.saveAndNewRetention) {
						// retain these properties and keep the drawer open
						const retainProps = {
							isMilestone: this.state.editTask.isMilestone,
							jobTitleId: this.state.editTask.jobTitleId,
							departmentId: this.state.editTask.departmentId,
							communityId: this.state.editTask.communityId,
							phaseId: this.state.editTask.phaseId,
						};

						const newTask = { ...this.state.newTask, ...retainProps };

						this.setState({ editTask: newTask, isLoading: false });
						this.props.onUpdateList();
					} else {
						// regular old save just closes the drawer
						this.props.onComplete();
					}
				} else if (response.type === types.CREATE_BASELINE_TASK_FAILURE) {
					Notification('error', 'An Error Occurred', response.payload.message);
					this.setState({ isLoading: false });
				}
			} catch (error) {
				Notification('error', 'An Error Occurred', error);
				this.setState({ isLoading: false });
			}
		} else {
			try {
				const response = await this.props.baselineTaskActions.saveBaselineTask(task);
				if (response.type === types.UPDATE_BASELINE_TASK_SUCCESS) {
					Notification('success', 'Success', 'Baseline Task Updated');
					this.props.onComplete();
				} else if (response.type === types.UPDATE_BASELINE_TASK_FAILURE) {
					Notification('error', 'An Error Occurred', response.payload.message);
					this.setState({ isLoading: false });
				}
			} catch (error) {
				Notification('error', 'An Error Occurred', error);
				this.setState({ isLoading: false });
			}
		}
	};

	renderFooter = () => {
		if (this.props.isMobile) {
			return (
				<div>
					<div className='button-wrap'>
						{this.state.editTask.id > 0 && this.state.unsavedChanges ? <span>You have unsaved changes</span> : ''}
					</div>
					<div className='button-wrap'>
						{' '}
						<button type='button' className='btn btn-primary button--save' onClick={this.onSave}>
							{this.state.editTask.id === 0 ? 'Save' : 'Apply Changes'}
						</button>
						<button type='button' className='btn btn-secondary' onClick={this.props.onCancel}>
							Cancel
						</button>
					</div>
				</div>
			);
		} else {
			return (
				<div className='button-wrap'>
					{this.state.editTask.id > 0 && this.state.unsavedChanges ? <span>You have unsaved changes</span> : ''}
					<button type='button' className='btn btn-primary button--save' onClick={this.onSave}>
						{this.state.editTask.id === 0 ? 'Save' : 'Apply Changes'}
					</button>
					{this.state.editTask.id === 0 && (
						<button type='button' className='btn btn-primary button--saveNew' onClick={this.onSaveAndNew}>
							Save and New
						</button>
					)}
					<button type='button' className='btn btn-secondary' onClick={this.props.onCancel}>
						Cancel
					</button>
				</div>
			);
		}
	};

	render() {
		const customFooter = this.renderFooter();

		return (
			<div>
				<Drawer
					isWide={true}
					title='Baseline Task'
					onSaveClick={this.onSave}
					saveText='Save'
					onCancelClick={this.props.onCancel}
					customFooter={customFooter}>
					<LoadingIndicator isLoading={this.state.isLoading}>
						<BaselineTaskForm
							task={this.state.editTask}
							filters={this.state.filters}
							isFormValid={this.state.isFormValid}
							showDateError={this.state.showDateError}
							linksShown={this.state.editTask.linksShown}
							onFormChange={this.onFormChange}
							onContingentChange={this.onContingentChange}
							onContingentRemove={this.onContingentRemove}
							onRemovePrereq={this.onRemovePrereq}
							onPrereqChange={this.onPrereqChange}
							formFilters={this.props.formFilters}
							taskSearch={this.searchTasks}
							taskPrereqSearch={this.searchPrereqTasks}
							taskSearchResults={this.state.taskSearchResults}
							taskPrereqSearchResults={this.state.taskPrereqSearchResults}
							baselineTaskActions={this.props.baselineTaskActions}
						/>
					</LoadingIndicator>
				</Drawer>
			</div>
		);
	}
}

BaselineTaskDrawer.propTypes = {
	subscriptionId: PropTypes.string.isRequired,
	taskId: PropTypes.number,
	baselineId: PropTypes.number.isRequired,
	onComplete: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
	onUpdateList: PropTypes.func.isRequired,
	baselineTaskActions: PropTypes.object.isRequired,
	formFilters: PropTypes.object.isRequired,
	isMobile: PropTypes.bool.isRequired,
};